import React, { useMemo, useState } from 'react'
import { addDays, format, parse, startOfDay } from 'date-fns'
import { Loading } from 'components/loading'
import { Input } from 'iebt-zing-storybook'
import i18n from 'sharedKernel/i18n/translate/config'
import { trans } from 'sharedKernel/i18n'
import { CustomQuestion } from 'domain/feature/hub/company/meeting-room/settings/ApplicationFormsTab/types'
import { INPUT_MASK_FORMAT, getPhoneMask } from 'sharedKernel/constants'
import { applyMaskIfNotEmpty } from 'sharedKernel/handlers'
import { EMAIL_REGEX } from 'sharedKernel/utils/validation'
import { minutesToTime } from 'sharedKernel/utils/format'
import { IPublicMeetingRoom } from '../types'
import { submitPublicReservationForRooms } from '../api/saveRequest'
import { IPublicFormSettings } from '../api/settingsMappers'
import { usePublicMeetingRoomSettings } from '../hooks/usePublicMeetingRoomSettings'
import { useApprovedReservations } from '../hooks/useApprovedReservations'
import { checkRoomsAvailability } from '../api/availabilityRequest'
import { YearMonth } from '../api/listByStatusRequest'
import { isDayFullyBlocked, isEndTimeBlocked, isStartTimeBlocked } from '../availabilityRules'
import {
  addBusinessDays,
  countInclusiveCalendarDays,
  getReservationDurationMinutes,
  isDayAllowed,
} from 'domain/feature/meeting-room/shared/workingDays'
import {
  resolvePurposeRules,
  selectionUsesEventRules,
} from '../purposeRules'
import { DynamicAnswerValue, DynamicQuestionField } from './DynamicQuestionField'
import { DatePickerField } from './DatePickerField'
import { TimePickerField } from './TimePickerField'
import { getClosedDaysAttentionMessage } from './closedDaysMessage'
import {
  AttendeesWrapper,
  CapacityIcon,
  CheckboxInput,
  CheckboxRow,
  CheckboxText,
  DateTimeRow,
  Divider,
  DynamicSection,
  FieldError,
  FieldFull,
  FieldGroup,
  FieldLabel,
  FieldsGrid,
  FormCard,
  FormCardDescription,
  FormCardTitle,
  RangeAttentionBanner,
  RequiredMark,
  SectionLabel,
  SelectedRoomCapacity,
  SelectedRoomChip,
  SelectedRoomName,
  SelectedRoomsRow,
  TermsCheckboxContent,
  TermsNote,
  TermsSection,
} from './style'

export const RESERVATION_FORM_ID = 'public-reservation-form'

export const requestReservationFormSubmit = (): void => {
  const form = document.getElementById(RESERVATION_FORM_ID)
  if (form instanceof HTMLFormElement) {
    form.requestSubmit()
  }
}

interface ReservationFormProps {
  selectedRooms: IPublicMeetingRoom[]
  onBack: () => void
  onSuccess: (requestNumber: string) => void
}

interface FormData {
  fullName: string
  organization: string
  email: string
  phone: string
  eventName: string
  description: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  attendees: string
  termsAccepted: boolean
  promoteOnWebsite: boolean
}

interface FormErrors {
  fullName?: string
  organization?: string
  email?: string
  phone?: string
  eventName?: string
  description?: string
  startDate?: string
  startTime?: string
  endDate?: string
  endTime?: string
  attendees?: string
  termsAccepted?: string
}

interface DateTimeFieldGroupProps {
  id: string
  label: string
  error?: string
  showError: boolean
  children: React.ReactNode
}

const DateTimeFieldGroup = ({
  id,
  label,
  error,
  showError,
  children,
}: DateTimeFieldGroupProps): JSX.Element => (
  <FieldGroup id={id}>
    <FieldLabel>
      {label}
      <RequiredMark>*</RequiredMark>
    </FieldLabel>
    {children}
    {showError && <FieldError>{error}</FieldError>}
  </FieldGroup>
)

const defaultFormData: FormData = {
  fullName: '',
  organization: '',
  email: '',
  phone: '',
  eventName: '',
  description: '',
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  attendees: '',
  termsAccepted: false,
  promoteOnWebsite: false,
}

const isAnswerEmpty = (value: DynamicAnswerValue | undefined): boolean => {
  if (value == null) return true
  if (Array.isArray(value)) return value.length === 0
  return value.trim() === ''
}

const formatAnswerForApi = (value: DynamicAnswerValue): string => {
  if (Array.isArray(value)) return value.join(', ')
  return value.trim()
}

const getLocalTodayStr = (): string => format(new Date(), 'yyyy-MM-dd')

const getSmallestCapacityRoom = (rooms: IPublicMeetingRoom[]): IPublicMeetingRoom | null => {
  const roomsWithCapacity = rooms.filter(room => room.maxCapacity > 0)
  if (roomsWithCapacity.length === 0) return null
  return roomsWithCapacity.reduce((smallest, room) =>
    room.maxCapacity < smallest.maxCapacity ? room : smallest
  )
}

const toYearMonth = (date: Date): YearMonth => ({ year: date.getFullYear(), month: date.getMonth() + 1 })

// Meses de um período yyyy-MM-dd (inclusive), pra carregar as reservas aprovadas que o tocam.
const monthsBetween = (startDateStr: string, endDateStr: string): YearMonth[] => {
  if (!startDateStr) return []
  const start = parse(startDateStr, 'yyyy-MM-dd', new Date())
  const end = endDateStr && endDateStr >= startDateStr ? parse(endDateStr, 'yyyy-MM-dd', new Date()) : start
  const months: YearMonth[] = []
  for (
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    cursor <= end;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)
  ) {
    months.push(toYearMonth(cursor))
  }
  return months
}

const blockedRoomsMessage = (roomNames: string[]): string =>
  i18n.t('meetingRoom.public.form.validation.roomsUnavailable', { rooms: roomNames.join(', ') })

const getBackendErrorMessage = (error: unknown): string | undefined => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message
  return typeof message === 'string' && message.trim() !== '' ? message : undefined
}

const validateFixedFields = (
  data: FormData,
  checkboxRequired: boolean,
  isEvent: boolean,
  settings: IPublicFormSettings | null,
  selectedRooms: IPublicMeetingRoom[]
): FormErrors => {
  const errors: FormErrors = {}
  const purposeRules = resolvePurposeRules(selectedRooms, settings)

  if (!data.fullName.trim()) errors.fullName = trans('meetingRoom.public.form.validation.nameRequired')
  if (!data.organization.trim()) errors.organization = trans('meetingRoom.public.form.validation.orgRequired')
  if (!data.email.trim()) {
    errors.email = trans('meetingRoom.public.form.validation.emailRequired')
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = trans('meetingRoom.public.form.validation.emailInvalid')
  }
  if (!data.phone.trim()) errors.phone = trans('meetingRoom.public.form.validation.phoneRequired')
  if (isEvent && !data.eventName.trim()) {
    errors.eventName = trans('meetingRoom.public.form.validation.eventNameRequired')
  }
  if (!data.description.trim()) errors.description = trans('meetingRoom.public.form.validation.descriptionRequired')

  if (selectedRooms.length > purposeRules.spacesLimit) {
    errors.startDate = i18n.t('meetingRoom.public.form.validation.spacesLimitExceeded', {
      count: purposeRules.spacesLimit,
    })
  }

  const todayStr = getLocalTodayStr()
  const hasWorkingDays = settings?.workingDays != null
  const hasOperatingHours = Boolean(settings?.openTime && settings?.closeTime)
  const earliestStartDate = addBusinessDays(
    startOfDay(new Date()),
    purposeRules.minLeadTime,
    settings?.workingDays
  )
  const earliestStartStr = format(earliestStartDate, 'yyyy-MM-dd')

  if (!data.startDate) {
    errors.startDate = trans('meetingRoom.public.form.validation.startDateRequired')
  } else if (data.startDate < todayStr) {
    errors.startDate = trans('meetingRoom.public.form.validation.startDatePast')
  } else if (purposeRules.minLeadTime > 0 && data.startDate < earliestStartStr) {
    errors.startDate = i18n.t('meetingRoom.public.form.validation.minLeadTime', {
      count: purposeRules.minLeadTime,
    })
  } else if (hasWorkingDays && !isDayAllowed(data.startDate, settings!.workingDays)) {
    errors.startDate = trans('meetingRoom.public.form.validation.dayNotAllowed')
  }

  if (!data.endDate) {
    errors.endDate = trans('meetingRoom.public.form.validation.endDateRequired')
  } else if (data.startDate && data.endDate < data.startDate) {
    errors.endDate = trans('meetingRoom.public.form.validation.endDateBeforeStart')
  } else if (hasWorkingDays && !isDayAllowed(data.endDate, settings!.workingDays)) {
    errors.endDate = trans('meetingRoom.public.form.validation.dayNotAllowed')
  } else if (
    data.startDate &&
    countInclusiveCalendarDays(data.startDate, data.endDate) > purposeRules.daysLimit
  ) {
    errors.endDate = i18n.t('meetingRoom.public.form.validation.daysLimitExceeded', {
      count: purposeRules.daysLimit,
    })
  }

  if (!data.startTime) {
    errors.startTime = trans('meetingRoom.public.form.validation.startTimeRequired')
  } else if (hasOperatingHours && (data.startTime < settings!.openTime || data.startTime >= settings!.closeTime)) {
    errors.startTime = trans('meetingRoom.public.form.validation.timeOutOfRange')
  }

  if (!data.endTime) {
    errors.endTime = trans('meetingRoom.public.form.validation.endTimeRequired')
  } else if (hasOperatingHours && (data.endTime <= settings!.openTime || data.endTime > settings!.closeTime)) {
    errors.endTime = trans('meetingRoom.public.form.validation.timeOutOfRange')
  } else if (data.startTime && data.startDate && data.endDate && data.startDate === data.endDate && data.endTime <= data.startTime) {
    errors.endTime = trans('meetingRoom.public.form.validation.endTimeBeforeStart')
  } else if (
    purposeRules.maxDurationMinutes != null &&
    data.startDate &&
    data.startTime &&
    data.endDate &&
    data.endTime
  ) {
    const durationMinutes = getReservationDurationMinutes(
      data.startDate,
      data.startTime,
      data.endDate,
      data.endTime
    )
    if (durationMinutes != null && durationMinutes > purposeRules.maxDurationMinutes) {
      errors.endTime = i18n.t('meetingRoom.public.form.validation.maxDurationExceeded', {
        duration: minutesToTime(purposeRules.maxDurationMinutes),
      })
    }
  }

  if (!data.attendees.trim() || Number(data.attendees) <= 0) {
    errors.attendees = trans('meetingRoom.public.form.validation.attendeesRequired')
  } else {
    const smallestRoom = getSmallestCapacityRoom(selectedRooms)
    if (smallestRoom && Number(data.attendees) > smallestRoom.maxCapacity) {
      errors.attendees = i18n.t('meetingRoom.public.form.validation.attendeesExceedsCapacity', {
        name: smallestRoom.name,
        count: smallestRoom.maxCapacity,
      })
    }
  }
  if (checkboxRequired && !data.termsAccepted) {
    errors.termsAccepted = trans('meetingRoom.public.form.validation.termsRequired')
  }
  return errors
}

const validateDynamicQuestions = (
  questions: CustomQuestion[],
  answers: Record<string, DynamicAnswerValue>
): Record<string, string> => {
  const errors: Record<string, string> = {}
  questions.forEach(question => {
    if (!question.id || !question.required) return
    if (isAnswerEmpty(answers[question.id])) {
      errors[question.id] = trans('meetingRoom.public.form.validation.questionRequired')
    }
  })
  return errors
}

const getAllQuestions = (settings: IPublicFormSettings | null): CustomQuestion[] =>
  settings?.sections.flatMap(section => section.questions) ?? []

const buildQuestionAnswersPayload = (
  questions: CustomQuestion[],
  answers: Record<string, DynamicAnswerValue>
): Array<{ questionId: string; answer: string }> =>
  questions
    .filter(question => question.id && !isAnswerEmpty(answers[question.id]))
    .map(question => ({
      questionId: question.id as string,
      answer: formatAnswerForApi(answers[question.id as string]),
    }))

export const ReservationForm = ({
  selectedRooms,
  onBack,
  onSuccess,
}: ReservationFormProps): JSX.Element => {
  const { settings, isLoading: isSettingsLoading } = usePublicMeetingRoomSettings()
  const [formData, setFormData] = useState<FormData>(defaultFormData)
  const [dynamicAnswers, setDynamicAnswers] = useState<Record<string, DynamicAnswerValue>>({})
  const [errors, setErrors] = useState<FormErrors>({})
  const [dynamicErrors, setDynamicErrors] = useState<Record<string, string>>({})
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEvent = useMemo(
    () => selectionUsesEventRules(selectedRooms),
    [selectedRooms]
  )
  const purposeRules = useMemo(
    () => resolvePurposeRules(selectedRooms, settings),
    [selectedRooms, settings]
  )
  const today = useMemo(() => startOfDay(new Date()), [])
  const startMinDate = useMemo(
    () => addBusinessDays(today, purposeRules.minLeadTime, settings?.workingDays),
    [today, purposeRules.minLeadTime, settings?.workingDays]
  )
  const endMinDate = formData.startDate
    ? parse(formData.startDate, 'yyyy-MM-dd', today)
    : startMinDate
  const endMaxDate = formData.startDate
    ? addDays(parse(formData.startDate, 'yyyy-MM-dd', today), Math.max(purposeRules.daysLimit - 1, 0))
    : undefined

  const dynamicQuestions = useMemo(() => getAllQuestions(settings), [settings])
  const acceptanceText = settings?.checkboxText?.trim()
    ? settings.checkboxText
    : trans('meetingRoom.public.form.termsAccept')
  const warningText = settings?.warningText?.trim()
    ? settings.warningText
    : trans('meetingRoom.public.form.termsNote')
  const isCheckboxRequired = settings?.checkboxRequired ?? true
  const showPromoteOnWebsite = isEvent && purposeRules.eventInterestDemonstration

  // PLA-3423: reservas aprovadas das salas selecionadas bloqueiam dias/horários nos seletores.
  const [visibleMonths, setVisibleMonths] = useState<YearMonth[]>([])
  const selectedRoomIds = useMemo(() => selectedRooms.map(room => room.id), [selectedRooms])
  const approvedMonths = useMemo(
    () => [toYearMonth(startMinDate), ...visibleMonths, ...monthsBetween(formData.startDate, formData.endDate)],
    [startMinDate, visibleMonths, formData.startDate, formData.endDate]
  )
  const { slots: approvedSlots, refetch: refetchApprovedSlots } = useApprovedReservations(
    selectedRoomIds,
    approvedMonths
  )
  const handleVisibleMonthChange = (date: Date): void => {
    setVisibleMonths(prev => [...prev, toYearMonth(date)])
  }
  const isDateBlocked = (date: string): boolean =>
    isDayFullyBlocked(approvedSlots, date, settings?.openTime || undefined, settings?.closeTime || undefined)
  const isStartTimeAvailable = (time: string): boolean =>
    !isStartTimeBlocked(approvedSlots, formData.startDate, time)
  const isEndTimeAvailable = (time: string): boolean =>
    !isEndTimeBlocked(
      approvedSlots,
      { startDate: formData.startDate, endDate: formData.endDate, startTime: formData.startTime },
      time
    )

  const closedDaysAttentionMessage = useMemo(
    () =>
      getClosedDaysAttentionMessage(
        formData.startDate,
        formData.endDate,
        settings?.workingDays
      ),
    [formData.endDate, formData.startDate, settings?.workingDays]
  )

  const showFieldError = (field: keyof FormErrors): boolean =>
    hasAttemptedSubmit && Boolean(errors[field])

  const showDynamicError = (questionId: string): boolean =>
    hasAttemptedSubmit && Boolean(dynamicErrors[questionId])

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>): void => {
    if (e.key !== 'Enter' || e.target instanceof HTMLTextAreaElement) return
    e.preventDefault()
  }

  const setField = (field: keyof FormData, value: string | boolean): void => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const setDynamicAnswer = (questionId: string, value: DynamicAnswerValue): void => {
    setDynamicAnswers(prev => ({ ...prev, [questionId]: value }))
    if (dynamicErrors[questionId]) {
      setDynamicErrors(prev => {
        const next = { ...prev }
        delete next[questionId]
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setHasAttemptedSubmit(true)

    const fixedErrors = validateFixedFields(formData, isCheckboxRequired, isEvent, settings, selectedRooms)
    const questionErrors = validateDynamicQuestions(dynamicQuestions, dynamicAnswers)
    const hasFixedErrors = Object.keys(fixedErrors).length > 0
    const hasQuestionErrors = Object.keys(questionErrors).length > 0

    if (hasFixedErrors || hasQuestionErrors) {
      setErrors(fixedErrors)
      setDynamicErrors(questionErrors)

      const firstFixedErrorKey = Object.keys(fixedErrors)[0]
      const firstQuestionErrorKey = Object.keys(questionErrors)[0]
      const scrollTargetId = firstFixedErrorKey
        ? `res-form-${firstFixedErrorKey}`
        : firstQuestionErrorKey
          ? `res-form-question-${firstQuestionErrorKey}`
          : null

      if (scrollTargetId) {
        document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    setIsSubmitting(true)
    try {
      // PLA-3423: pré-checagem tudo-ou-nada no backend antes de qualquer envio — é ela (sempre atual)
      // que decide no envio; as reservas aprovadas em cache só desabilitam opções nos seletores. Se
      // ela falhar (rede, rota indisponível no legado), segue — o 409 do backend continua protegendo.
      const blockedRoomIds = await checkRoomsAvailability({
        meetingRoomIds: selectedRooms.map(r => r.id),
        reservationDate: formData.startDate,
        endDate: formData.endDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
      }).catch((): string[] => [])

      if (blockedRoomIds.length > 0) {
        setErrors(prev => ({
          ...prev,
          startTime: blockedRoomsMessage(
            selectedRooms.filter(room => blockedRoomIds.includes(room.id)).map(room => room.name)
          ),
        }))
        document.getElementById('res-form-startTime')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        refetchApprovedSlots()
        return
      }

      const responses = await submitPublicReservationForRooms(
        selectedRooms.map(r => r.id),
        {
          name: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          companyName: formData.organization.trim(),
          reservationDate: formData.startDate,
          endDate: formData.endDate,
          startTime: formData.startTime,
          endTime: formData.endTime,
          institutionType: 'External',
          eventName: formData.eventName.trim(),
          eventDescription: formData.description.trim(),
          numberAttendees: Number(formData.attendees),
          isPublic: false,
          promoteOnWebsite: showPromoteOnWebsite ? formData.promoteOnWebsite : false,
          questionAnswers: buildQuestionAnswersPayload(dynamicQuestions, dynamicAnswers),
        }
      )
      const requestNumber = responses
        .map(response => response.code || response.cancelToken || response.id)
        .filter(Boolean)
        .join(', ')
      onSuccess(requestNumber)
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        termsAccepted: getBackendErrorMessage(error) ?? trans('meetingRoom.public.form.submitError'),
      }))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSettingsLoading && settings == null) {
    return <Loading />
  }

  return (
    <FormCard>
      <FormCardTitle>{trans('meetingRoom.public.form.sectionTitle')}</FormCardTitle>
      <FormCardDescription>{trans('meetingRoom.public.form.sectionDescription')}</FormCardDescription>

      <SelectedRoomsRow>
        {selectedRooms.map(room => (
          <SelectedRoomChip key={room.id}>
            <SelectedRoomName>{room.name}</SelectedRoomName>
            <SelectedRoomCapacity>
              <CapacityIcon icon={['fas', 'signal']} />
              {i18n.t('meetingRoom.public.capacity', { count: room.maxCapacity })}
            </SelectedRoomCapacity>
          </SelectedRoomChip>
        ))}
      </SelectedRoomsRow>

      <Divider />

      <form
        id={RESERVATION_FORM_ID}
        onSubmit={e => { void handleSubmit(e) }}
        onKeyDown={handleFormKeyDown}
        noValidate
      >
        <SectionLabel>{trans('meetingRoom.public.form.generalInfoSection')}</SectionLabel>

        <FieldsGrid>
          <FieldFull>
            <Input
              id="res-form-fullName"
              showCounter={false}
              isRequired
              label={trans('meetingRoom.public.form.fullName')}
              value={formData.fullName}
              placeholder={trans('meetingRoom.public.form.fullNamePlaceholder')}
              isInvalid={showFieldError('fullName')}
              notValidFieldMessage={errors.fullName}
              onChange={e => setField('fullName', e.target.value)}
            />
          </FieldFull>

          <FieldFull>
            <Input
              id="res-form-organization"
              showCounter={false}
              isRequired
              label={trans('meetingRoom.public.form.organization')}
              value={formData.organization}
              placeholder={trans('meetingRoom.public.form.organizationPlaceholder')}
              isInvalid={showFieldError('organization')}
              notValidFieldMessage={errors.organization}
              onChange={e => setField('organization', e.target.value)}
            />
          </FieldFull>

          <Input
            id="res-form-email"
            code="email"
            showCounter={false}
            isRequired
            label={trans('meetingRoom.public.form.email')}
            value={formData.email}
            placeholder={trans('meetingRoom.public.form.emailPlaceholder')}
            isInvalid={showFieldError('email')}
            notValidFieldMessage={errors.email}
            onChange={e => setField('email', e.target.value)}
          />

          <Input
            id="res-form-phone"
            code="masked"
            showCounter={false}
            isRequired
            label={trans('meetingRoom.public.form.phone')}
            value={formData.phone}
            placeholder={trans('meetingRoom.public.form.phonePlaceholder')}
            mask={applyMaskIfNotEmpty(formData.phone, getPhoneMask(formData.phone) || INPUT_MASK_FORMAT.MOBILE)}
            isInvalid={showFieldError('phone')}
            notValidFieldMessage={errors.phone}
            onChange={e => setField('phone', e.target.value)}
          />

          {isEvent && (
            <FieldFull>
              <Input
                id="res-form-eventName"
                showCounter={false}
                isRequired
                label={trans('meetingRoom.public.form.eventName')}
                value={formData.eventName}
                placeholder={trans('meetingRoom.public.form.eventNamePlaceholder')}
                isInvalid={showFieldError('eventName')}
                notValidFieldMessage={errors.eventName}
                onChange={e => setField('eventName', e.target.value)}
              />
            </FieldFull>
          )}

          <FieldFull>
            <Input
              id="res-form-description"
              code="textarea"
              showCounter={false}
              isRequired
              label={trans('meetingRoom.public.form.description')}
              value={formData.description}
              placeholder={trans('meetingRoom.public.form.descriptionPlaceholder')}
              isInvalid={showFieldError('description')}
              notValidFieldMessage={errors.description}
              onChange={e => setField('description', e.target.value)}
            />
          </FieldFull>
        </FieldsGrid>

        <DateTimeRow>
          <DateTimeFieldGroup
            id="res-form-startDate"
            label={trans('meetingRoom.public.form.startDate')}
            error={errors.startDate}
            showError={showFieldError('startDate')}
          >
            <DatePickerField
              id="res-form-startDate-input"
              value={formData.startDate}
              onChange={v => {
                setFormData(prev => {
                  let nextEndDate = prev.endDate
                  if (nextEndDate && nextEndDate < v) {
                    nextEndDate = v
                  } else if (
                    nextEndDate &&
                    countInclusiveCalendarDays(v, nextEndDate) > purposeRules.daysLimit
                  ) {
                    nextEndDate = format(
                      addDays(parse(v, 'yyyy-MM-dd', today), Math.max(purposeRules.daysLimit - 1, 0)),
                      'yyyy-MM-dd'
                    )
                  }
                  return { ...prev, startDate: v, endDate: nextEndDate }
                })
                if (errors.startDate || errors.endDate) {
                  setErrors(prev => ({ ...prev, startDate: undefined, endDate: undefined }))
                }
              }}
              minDate={startMinDate}
              workingDays={settings?.workingDays}
              isDateBlocked={isDateBlocked}
              onMonthChange={handleVisibleMonthChange}
              isInvalid={showFieldError('startDate')}
            />
          </DateTimeFieldGroup>

          <DateTimeFieldGroup
            id="res-form-startTime"
            label={trans('meetingRoom.public.form.startTime')}
            error={errors.startTime}
            showError={showFieldError('startTime')}
          >
            <TimePickerField
              id="res-form-startTime-input"
              value={formData.startTime}
              onChange={v => setField('startTime', v)}
              minTime={settings?.openTime || undefined}
              maxTime={settings?.closeTime || undefined}
              filterTime={isStartTimeAvailable}
              isInvalid={showFieldError('startTime')}
            />
          </DateTimeFieldGroup>

          <DateTimeFieldGroup
            id="res-form-endDate"
            label={trans('meetingRoom.public.form.endDate')}
            error={errors.endDate}
            showError={showFieldError('endDate')}
          >
            <DatePickerField
              id="res-form-endDate-input"
              value={formData.endDate}
              onChange={v => setField('endDate', v)}
              minDate={endMinDate}
              maxDate={endMaxDate}
              workingDays={settings?.workingDays}
              isDateBlocked={isDateBlocked}
              onMonthChange={handleVisibleMonthChange}
              isInvalid={showFieldError('endDate')}
            />
          </DateTimeFieldGroup>

          <DateTimeFieldGroup
            id="res-form-endTime"
            label={trans('meetingRoom.public.form.endTime')}
            error={errors.endTime}
            showError={showFieldError('endTime')}
          >
            <TimePickerField
              id="res-form-endTime-input"
              value={formData.endTime}
              onChange={v => setField('endTime', v)}
              minTime={formData.startDate === formData.endDate && formData.startTime ? formData.startTime : (settings?.openTime || undefined)}
              maxTime={settings?.closeTime || undefined}
              filterTime={isEndTimeAvailable}
              isInvalid={showFieldError('endTime')}
            />
          </DateTimeFieldGroup>
        </DateTimeRow>

        {closedDaysAttentionMessage ? (
          <RangeAttentionBanner role="status">
            {closedDaysAttentionMessage}
          </RangeAttentionBanner>
        ) : null}

        <AttendeesWrapper>
          <Input
            id="res-form-attendees"
            code="number"
            type="number"
            showCounter={false}
            isRequired
            label={trans('meetingRoom.public.form.attendees')}
            value={formData.attendees}
            placeholder={trans('meetingRoom.public.form.attendeesPlaceholder')}
            isInvalid={showFieldError('attendees')}
            notValidFieldMessage={errors.attendees}
            onChange={e => setField('attendees', e.target.value.replace(/\D/g, ''))}
          />
        </AttendeesWrapper>

        {settings?.sections.map((section, sectionIndex) => (
          <DynamicSection key={section.id ?? `section-${sectionIndex}`}>
            {section.name.trim() ? (
              <SectionLabel>{section.name}</SectionLabel>
            ) : null}
            <FieldsGrid>
              {section.questions.map((question, questionIndex) => {
                if (!question.id) return null
                return (
                  <DynamicQuestionField
                    key={question.id}
                    question={question}
                    value={dynamicAnswers[question.id] ?? (question.type === 'checkbox' ? [] : '')}
                    hasError={showDynamicError(question.id)}
                    errorMessage={dynamicErrors[question.id]}
                    onChange={value => setDynamicAnswer(question.id as string, value)}
                  />
                )
              })}
            </FieldsGrid>
          </DynamicSection>
        ))}

        {showPromoteOnWebsite && (
          <TermsSection>
            <CheckboxRow htmlFor="res-form-promoteOnWebsite">
              <CheckboxInput
                id="res-form-promoteOnWebsite"
                type="checkbox"
                checked={formData.promoteOnWebsite}
                onChange={e => setField('promoteOnWebsite', e.target.checked)}
              />
              <TermsCheckboxContent>
                <CheckboxText>
                  {trans('meetingRoom.public.form.promoteOnWebsite')}
                </CheckboxText>
              </TermsCheckboxContent>
            </CheckboxRow>
          </TermsSection>
        )}

        <TermsSection>
          <CheckboxRow htmlFor="res-form-termsAccepted">
            <CheckboxInput
              id="res-form-termsAccepted"
              type="checkbox"
              checked={formData.termsAccepted}
              onChange={e => setField('termsAccepted', e.target.checked)}
            />
            <TermsCheckboxContent>
              <CheckboxText>
                {acceptanceText}
                {isCheckboxRequired && <RequiredMark>*</RequiredMark>}
              </CheckboxText>
              <TermsNote>{warningText}</TermsNote>
              {showFieldError('termsAccepted') && (
                <FieldError>{errors.termsAccepted}</FieldError>
              )}
            </TermsCheckboxContent>
          </CheckboxRow>
        </TermsSection>
      </form>
    </FormCard>
  )
}
